import type { AdmissionOfficeCountry } from "../../admissionOfficeCountry";
import {
  listMaltaFrenchAdmissionSignatures,
  templatesMaltaFrenchAdmissionCoursesSettings,
  templatesMaltaFrenchInvoiceCoursesSettings,
} from "@/src/constants/AdmissionOffice/Malta/AOMaltaFrConsts";
import {
  listMaltaGermanAdmissionSignatures,
  templatesMaltaGermanAdmissionCoursesSettings,
  templatesMaltaGermanInvoiceCoursesSettings,
} from "@/src/constants/AdmissionOffice/Malta/AOMaltaDeConsts";
import {
  listMaltaUzbAdmissionSignatures,
  templatesMaltaUzbAdmissionCoursesSettings,
  templatesMaltaUzbInvoiceCoursesSettings,
} from "@/src/constants/AdmissionOffice/Malta/AOMaltaUzbConsts";

export type DoubleLangCountry = Extract<AdmissionOfficeCountry, "malta">;
export type SecondLangCode = "fr" | "de" | "uzb";

type AdmissionTemplate = {
  tinyMCEContent: string;
  tinyMCEContentGerman?: string;
  tinyMCEContentFranch?: string;
  tinyMCEContentRussian?: string;
  selected_course: string;
  selected_course_german?: string;
  selected_course_franch?: string;
  selected_course_russian?: string;
  medium_of_instruction: string;
  medium_of_instruction_german?: string;
  medium_of_instruction_franch?: string;
  medium_of_instruction_russian?: string;
  duration: string;
  duration_german?: string;
  duration_franch?: string;
  duration_russian?: string;
  title: string;
};

type InvoiceTemplate = {
  tinyMCEContent: string;
  tinyMCEContentGerman?: string;
  tinyMCEContentFranch?: string;
  tinyMCEContentRussian?: string;
};

type DoubleLangRegistryEntry = {
  admissionTemplates: Record<string, AdmissionTemplate>;
  invoiceTemplates: Record<string, InvoiceTemplate>;
  signatures: Record<string, string>;
  labels: {
    admissionTitle: string;
    invoiceTitle: string;
    fullName: string;
    birthDate: string;
    nationality: string;
    course: string;
    medium: string;
    duration: string;
    signatureTop: string;
    signatureBottom: string;
    datePrefix: string;
  };
};

const MALTA_ADMISSION_DOUBLE_LANG_REGISTRY: Record<SecondLangCode, DoubleLangRegistryEntry> = {
  fr: {
    admissionTemplates: templatesMaltaFrenchAdmissionCoursesSettings as Record<string, AdmissionTemplate>,
    invoiceTemplates: templatesMaltaFrenchInvoiceCoursesSettings as Record<string, InvoiceTemplate>,
    signatures: listMaltaFrenchAdmissionSignatures,
    labels: {
      admissionTitle: "LETTRE D'ADMISSION/CONFIRMATION",
      invoiceTitle: "FACTURE",
      fullName: "Nom complet",
      birthDate: "Date de naissance",
      nationality: "Nationalité",
      course: "Cours",
      medium: "Langue d'instruction",
      duration: "Durée",
      signatureTop: "Chef du Département des Admissions",
      signatureBottom: "Université Internationale Européenne",
      datePrefix: "Date",
    },
  },
  de: {
    admissionTemplates: templatesMaltaGermanAdmissionCoursesSettings as Record<string, AdmissionTemplate>,
    invoiceTemplates: templatesMaltaGermanInvoiceCoursesSettings as Record<string, InvoiceTemplate>,
    signatures: listMaltaGermanAdmissionSignatures,
    labels: {
      admissionTitle: "ZULASSUNGS/BESTÄTIGUNGSBRIEF",
      invoiceTitle: "RECHNUNG",
      fullName: "Vollständiger Name",
      birthDate: "Geburtsdatum",
      nationality: "Staatsangehörigkeit",
      course: "Studiengang",
      medium: "Unterrichtssprache",
      duration: "Dauer",
      signatureTop: "Leiterin der Zulassungsabteilung",
      signatureBottom: "International European University",
      datePrefix: "Datum",
    },
  },
  uzb: {
    admissionTemplates: templatesMaltaUzbAdmissionCoursesSettings as Record<string, AdmissionTemplate>,
    invoiceTemplates: templatesMaltaUzbInvoiceCoursesSettings as Record<string, InvoiceTemplate>,
    signatures: listMaltaUzbAdmissionSignatures,
    labels: {
      admissionTitle: "Письмо о возможности зачисления",
      invoiceTitle: "Фактура",
      fullName: "ФИО",
      birthDate: "Дата рождения",
      nationality: "Национальность",
      course: "Курс",
      medium: "Язык обучения",
      duration: "Продолжительность",
      signatureTop: "Начальник международного отдела",
      signatureBottom: "Международный европейский университет",
      datePrefix: "Дата",
    },
  },
};

export const getAdmissionDoubleLangRegistry = (country: DoubleLangCountry, secondLang: SecondLangCode) => {
  if (country !== "malta") {
    throw new Error(`Double language admission is not configured for country '${country}'`);
  }

  return MALTA_ADMISSION_DOUBLE_LANG_REGISTRY[secondLang];
};

export const getSecondLangFieldLabel = (secondLang: SecondLangCode) => (secondLang === "uzb" ? "RU" : secondLang.toUpperCase());

export const pickAdmissionSecondLangFields = (secondLang: SecondLangCode, template: AdmissionTemplate) => {
  if (secondLang === "de") {
    return {
      body: template.tinyMCEContentGerman || "",
      course: template.selected_course_german || "",
      medium: template.medium_of_instruction_german || "",
      duration: template.duration_german || "",
    };
  }

  if (secondLang === "uzb") {
    return {
      body: template.tinyMCEContentRussian || "",
      course: template.selected_course_russian || "",
      medium: template.medium_of_instruction_russian || "",
      duration: template.duration_russian || "",
    };
  }

  return {
    body: template.tinyMCEContentFranch || "",
    course: template.selected_course_franch || "",
    medium: template.medium_of_instruction_franch || "",
    duration: template.duration_franch || "",
  };
};

export const pickInvoiceSecondLangBody = (secondLang: SecondLangCode, template: InvoiceTemplate) => {
  if (secondLang === "de") return template.tinyMCEContentGerman || "";
  if (secondLang === "uzb") return template.tinyMCEContentRussian || "";
  return template.tinyMCEContentFranch || "";
};
